import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import SignatureCapture from "@/components/SignatureCapture";
import { CheckCircle, CaretDown, CaretRight, Printer } from "@phosphor-icons/react";

export default function Reconciliation() {
  const { user } = useAuth();
  const [year, setYear] = useState(new Date().getFullYear());
  const [rows, setRows] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(null);
  const [sig, setSig] = useState("");
  const [statuses, setStatuses] = useState({});
  const [loading, setLoading] = useState(true);
  const [adjustOpen, setAdjustOpen] = useState(null);
  const [adjForm, setAdjForm] = useState({ manual_override: "", carryover: "" });
  const canAdjust = ["hr", "admin"].includes(user?.role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/leaves/reconciliation?year=${year}`);
      setRows(data.rows);
      // fetch confirmation statuses for each row
      const st = {};
      await Promise.all(data.rows.map(async (r) => {
        try {
          const { data: s } = await api.get(`/leaves/reconciliation/${r.user_id}/status?year=${year}`);
          st[r.user_id] = s;
        } catch { /* noop */ }
      }));
      setStatuses(st);
    } finally {
      setLoading(false);
    }
  }, [year]);
  useEffect(() => { load(); }, [load]);

  const doConfirm = async () => {
    if (!sig) return toast.error("İmza gerekli");
    try {
      await api.post(`/leaves/reconciliation/${confirmOpen.user_id}/confirm`, { signature: sig });
      toast.success("Yıllık izin mütabakatı imzalandı");
      setConfirmOpen(null); setSig("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Hata");
    }
  };

  const toggleExpand = (uid) => setExpanded((e) => ({ ...e, [uid]: !e[uid] }));
  const canConfirmSelf = (row) => row.user_id === user?.id && !statuses[row.user_id]?.signature;

  const openAdjust = (row) => {
    setAdjForm({
      manual_override: (row.manual_override === null || row.manual_override === undefined) ? "" : String(row.manual_override),
      carryover: row.carryover ? String(row.carryover) : "",
    });
    setAdjustOpen(row);
  };
  const doAdjust = async () => {
    try {
      await api.post(`/leaves/reconciliation/${adjustOpen.user_id}/adjust`, {
        manual_override: adjForm.manual_override === "" ? null : Number(adjForm.manual_override),
        carryover: adjForm.carryover === "" ? 0 : Number(adjForm.carryover),
      });
      toast.success("İzin hakkı güncellendi");
      setAdjustOpen(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Hata");
    }
  };

  // Build printable HTML for one (or all) reconciliation rows and open in a new tab.
  // Each person is on its own page (page-break-after: always) so the user gets separate
  // sheets when printing.
  const buildPrintDocument = (targets) => {
    const doc = `<!doctype html><html lang="tr"><head><meta charset="utf-8">
<title>Yıllık İzin Mütabakatı ${year}</title>
<style>
  @page { size: A4; margin: 18mm 14mm; }
  body { font-family: 'Georgia', 'Times New Roman', serif; color: #0B2027; margin: 0; }
  .sheet { padding: 12mm; page-break-after: always; }
  .sheet:last-child { page-break-after: auto; }
  .brand { font-size: 11pt; letter-spacing: 0.2em; text-transform: uppercase; color: #6B7280; margin-bottom: 4mm; }
  h1 { font-size: 18pt; margin: 0 0 6mm; letter-spacing: -0.01em; }
  .who { border: 1px solid #d1d5db; border-radius: 4px; padding: 4mm 5mm; margin-bottom: 5mm; }
  .who .name { font-size: 14pt; font-weight: bold; }
  .who .meta { font-size: 10pt; color: #6B7280; margin-top: 1mm; }
  .stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 3mm; margin-bottom: 5mm; }
  .stat { border: 1px solid #d1d5db; border-radius: 4px; padding: 3mm 4mm; text-align: center; }
  .stat .lbl { font-size: 9pt; text-transform: uppercase; letter-spacing: 0.1em; color: #6B7280; }
  .stat .val { font-size: 20pt; font-weight: bold; margin-top: 1mm; }
  .stat.warn .val { color: #b91c1c; }
  table { width: 100%; border-collapse: collapse; font-size: 10pt; margin-bottom: 5mm; }
  th, td { border: 1px solid #d1d5db; padding: 2mm 3mm; text-align: left; }
  th { background: #f4f1ea; text-transform: uppercase; font-size: 9pt; letter-spacing: 0.05em; }
  .footer { display: flex; justify-content: space-between; margin-top: 10mm; gap: 6mm; }
  .sig-box { flex: 1; border-top: 1px solid #0B2027; padding-top: 2mm; font-size: 9pt; text-align: center; }
  .sig-box img { max-height: 18mm; margin-bottom: 1mm; }
  .note { font-size: 8.5pt; color: #6B7280; margin-top: 4mm; line-height: 1.5; }
</style></head><body>
${targets.map((r) => {
  const st = statuses[r.user_id] || {};
  const leavesRows = r.leaves.length === 0
    ? `<tr><td colspan="4" style="text-align:center;color:#6B7280;">${year} yılında yıllık izin kaydı bulunmuyor.</td></tr>`
    : r.leaves.map((lv) => `<tr>
        <td>${lv.start_date || ""}</td>
        <td>${lv.end_date || ""}</td>
        <td>${lv.day_count ?? ""}</td>
        <td>${(lv.approved_at || "").slice(0, 10) || "-"}</td>
      </tr>`).join("");
  const remainCls = (r.remaining < 0) ? "stat warn" : "stat";
  return `<div class="sheet">
    <div class="brand">Colossae Hotel · Colossae Personel</div>
    <h1>Yıllık İzin Mütabakat Formu — ${year}</h1>
    <div class="who">
      <div class="name">${r.name}</div>
      <div class="meta">${r.department || "-"} · ${r.position || "-"}</div>
    </div>
    <div class="stats">
      <div class="stat"><div class="lbl">Hak Edilen</div><div class="val">${r.entitled}</div></div>
      <div class="stat"><div class="lbl">Kullanılan</div><div class="val">${r.used}</div></div>
      <div class="${remainCls}"><div class="lbl">Kalan</div><div class="val">${r.remaining}</div></div>
    </div>
    <table>
      <thead><tr><th>Başlangıç</th><th>İş Başı</th><th>Gün</th><th>Onay Tarihi</th></tr></thead>
      <tbody>${leavesRows}</tbody>
    </table>
    <div class="footer">
      <div class="sig-box">
        ${st.signature ? `<img src="${st.signature}" alt="imza"/>` : `<div style="height:18mm;"></div>`}
        <div>Personel İmzası — ${r.name}</div>
        ${st.confirmed_at ? `<div style="font-size:8pt;color:#6B7280;">${new Date(st.confirmed_at).toLocaleString("tr-TR")}</div>` : ""}
      </div>
      <div class="sig-box">
        <div style="height:18mm;"></div>
        <div>İnsan Kaynakları / İdari Amir</div>
      </div>
    </div>
    <div class="note">Bu form, ${year} yılı yıllık ücretli izin hak, kullanım ve kalan gün sayılarının Türk İş Kanunu 53. madde ve devamı hükümleri uyarınca personel ve işveren arasında mutabık kalındığını beyan etmek amacıyla düzenlenmiştir. İmzalayan personel, yukarıdaki bilgilerin doğruluğunu kabul ettiğini beyan eder.</div>
  </div>`;
}).join("")}
<script>window.onload = function(){ setTimeout(function(){ window.print(); }, 350); };</script>
</body></html>`;
    const w = window.open("", "_blank");
    if (!w) { toast.error("Popup engellendi — tarayıcı ayarlarından popupları izin verin"); return; }
    w.document.open();
    w.document.write(doc);
    w.document.close();
  };

  const printPerson = (row) => buildPrintDocument([row]);
  const printAll = () => {
    if (!rows.length) return toast.error("Yazdıracak kayıt yok");
    buildPrintDocument(rows);
  };

  const canManage = ["admin", "hr", "gm"].includes(user?.role);
  const canYearEnd = ["admin", "hr"].includes(user?.role);

  const exportExcel = async () => {
    try {
      const r = await api.get(`/leaves/reconciliation/export?year=${year}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `yillik_izin_mutabakat_${year}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Excel indiriliyor");
    } catch { toast.error("Excel dışa aktarılamadı"); }
  };

  const doYearEnd = async () => {
    if (!window.confirm(`${year} yılının KALAN günleri tüm personelin 'devir'ine taşınacak ve bu yılın manuel hakları sıfırlanacak. Onaylıyor musunuz?`)) return;
    try {
      const { data } = await api.post(`/leaves/reconciliation/year-end-carryover?year=${year}`);
      toast.success(`${data.updated} personelin kalan günü devire taşındı`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-end flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Yıl Sonu</div>
          <h1 className="font-display text-3xl font-bold mt-1">Yıllık İzin Mütabakatı</h1>
        </div>
        <div className="flex gap-2 items-center">
          <Input type="number" min={2020} max={2100} value={year} onChange={(e) => setYear(Number(e.target.value))} className="w-28" data-testid="rec-year" />
          {canManage && <Button variant="outline" onClick={exportExcel} data-testid="rec-export-excel">Excel'e Aktar</Button>}
          {canYearEnd && <Button variant="outline" onClick={doYearEnd} data-testid="rec-year-end">Yıl Sonu Devri</Button>}
          <Button variant="outline" onClick={printAll} data-testid="rec-print"><Printer size={16} className="mr-1" />Tümünü Yazdır (ayrı sayfa)</Button>
        </div>
      </div>

      {loading && <div className="text-muted-foreground text-sm">Yükleniyor…</div>}

      <div className="printable space-y-3">
        {rows.map((r) => {
          const st = statuses[r.user_id];
          return (
            <Card key={r.user_id}>
              <CardContent className="p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <Button size="icon" variant="ghost" onClick={() => toggleExpand(r.user_id)} data-testid={`rec-toggle-${r.user_id}`}>
                      {expanded[r.user_id] ? <CaretDown size={16} /> : <CaretRight size={16} />}
                    </Button>
                    <div>
                      <div className="font-semibold">{r.name}</div>
                      <div className="text-xs text-muted-foreground">{r.department} · {r.position}</div>
                    </div>
                  </div>
                  <div className="flex gap-4 items-center flex-wrap">
                    <div className="text-sm"><span className="text-muted-foreground">Hak:</span> <b>{r.entitled}</b>{r.manual_override !== null && r.manual_override !== undefined && <Badge variant="outline" className="ml-1 text-[9px]">manuel</Badge>}</div>
                    <div className="text-sm"><span className="text-muted-foreground">Kullanılan:</span> <b>{r.used}</b></div>
                    <div className="text-sm"><span className="text-muted-foreground">Kalan:</span> <b className={r.remaining < 0 ? "text-destructive" : ""}>{r.remaining}</b></div>
                    <div className="text-sm" title={r.next_anniversary_date ? `Yıldönümü: ${r.next_anniversary_date}` : ""}>
                      <span className="text-muted-foreground">Gelecek Yıl Hak:</span> <b className="text-primary">{r.next_year_entitlement ?? "-"}</b>
                    </div>
                    {st?.signature ? (
                      <Badge className="gap-1"><CheckCircle size={12} /> İmzalandı — {st.confirmed_at?.slice(0, 10)}</Badge>
                    ) : (
                      <Badge variant="outline">İmza bekleniyor</Badge>
                    )}
                    {canAdjust && (
                      <Button size="sm" variant="outline" onClick={() => openAdjust(r)} data-testid={`rec-adjust-${r.user_id}`}>Manuel Düzelt</Button>
                    )}
                    {canConfirmSelf(r) && (
                      <Button size="sm" onClick={() => setConfirmOpen(r)} data-testid={`rec-confirm-${r.user_id}`}>Mütabık İmzala</Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => printPerson(r)} data-testid={`rec-print-${r.user_id}`}>
                      <Printer size={12} className="mr-1" />Yazdır
                    </Button>
                  </div>
                </div>

                {expanded[r.user_id] && (
                  <div className="mt-3 border-t border-border pt-3">
                    <div className="text-xs uppercase text-muted-foreground mb-2">{year} yılı onaylı yıllık izinleri</div>
                    {r.leaves.length === 0 ? (
                      <div className="text-sm text-muted-foreground">Bu yıl izin kullanılmamış.</div>
                    ) : (
                      <table className="w-full text-sm">
                        <thead className="text-xs uppercase text-muted-foreground">
                          <tr><th className="text-left py-1">Başlangıç</th><th className="text-left py-1">Bitiş</th><th className="text-left py-1">Gün</th><th className="text-left py-1">Onay Tarihi</th></tr>
                        </thead>
                        <tbody>
                          {r.leaves.map((lv) => (
                            <tr key={lv.id} className="border-t border-border">
                              <td className="py-1">{lv.start_date}</td>
                              <td className="py-1">{lv.end_date}</td>
                              <td className="py-1">{lv.day_count}</td>
                              <td className="py-1">{lv.approved_at?.slice(0, 10) || "-"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                    {st?.signature && (
                      <div className="mt-3 flex items-center gap-3">
                        <span className="text-xs uppercase text-muted-foreground">Personel imzası:</span>
                        <img src={st.signature} alt="imza" className="h-12 object-contain" />
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
        {rows.length === 0 && !loading && <div className="text-muted-foreground">Kayıt yok</div>}
      </div>

      <Dialog open={!!confirmOpen} onOpenChange={(o) => !o && setConfirmOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Yıllık İzin Mütabakatı</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            {year} yılı için hak: <b>{confirmOpen?.entitled}</b>, kullanılan: <b>{confirmOpen?.used}</b>, kalan: <b>{confirmOpen?.remaining}</b> gün.
            Bilgilerin doğruluğunu kabul ediyorsanız imzanızı ekleyip onaylayın.
          </p>
          <SignatureCapture onChange={setSig} testId="rec-signature" />
          <Button onClick={doConfirm} className="w-full" data-testid="rec-confirm-submit">Mütabık — İmzala</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!adjustOpen} onOpenChange={(o) => !o && setAdjustOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>İzin Hakkını Manuel Düzelt — {adjustOpen?.name}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Yalnızca İnsan Kaynakları düzenleyebilir. Kullanılan/kalan gün, onaylanan yıllık izinlerden otomatik hesaplanır; burada yalnızca <b>hak ediş</b> düzeltilir.
          </p>
          <div className="space-y-3 mt-1">
            <div>
              <label className="text-xs uppercase text-muted-foreground">Bu Yıl Net Hak (gün)</label>
              <Input type="number" step="0.5" value={adjForm.manual_override} placeholder={`Boş = otomatik (${adjustOpen?.auto_entitled ?? "-"} gün)`}
                     onChange={(e) => setAdjForm({ ...adjForm, manual_override: e.target.value })} data-testid="rec-adj-manual" />
              <div className="text-[11px] text-muted-foreground mt-1">Boş bırakılırsa kıdem/yaş kuralına göre otomatik hesaplanır.</div>
            </div>
            <div>
              <label className="text-xs uppercase text-muted-foreground">Geçmiş Yıldan Devir (gün)</label>
              <Input type="number" step="0.5" value={adjForm.carryover} placeholder="0"
                     onChange={(e) => setAdjForm({ ...adjForm, carryover: e.target.value })} data-testid="rec-adj-carryover" />
            </div>
          </div>
          <Button onClick={doAdjust} className="w-full" data-testid="rec-adj-submit">Kaydet</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
